/**
 * MuseControl - Control de conexión y calibración del Muse 2
 * 
 * Calibración mejorada con detección de parpadeos:
 * 1. Calidad de señal (verificar contacto) - 3s
 * 2. Parpadeos (detectar 5 parpadeos) - validación precisa
 * 3. Relajación (ojos abiertos + ojos cerrados) - baseline de alpha - 40s (20s + 20s)
 * 
 * También incluye grabación de sesiones EEG para reproducción posterior.
 */

import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, useBrainStore } from '../store';

const MUSE_STATUS = {
  DISCONNECTED: 'disconnected',
  SCANNING: 'scanning',
  CONNECTED: 'connected',
  STREAMING: 'streaming',
  CALIBRATING: 'calibrating',
  ERROR: 'error'
};

const CALIBRATION_STEPS = {
  IDLE: 'idle',
  SIGNAL_CHECK: 'signal_check',
  BLINK_TEST: 'blink_test',
  RELAXATION: 'relaxation',
  COMPLETE: 'complete',
  FAILED: 'failed'
};

export default function MuseControl({ onModeChange }) {
  const [status, setStatus] = useState(MUSE_STATUS.DISCONNECTED);
  const [error, setError] = useState(null);
  const [signalQuality, setSignalQuality] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  // Device picker
  const [discoveredDevices, setDiscoveredDevices] = useState([]);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [lastMuseAddress, setLastMuseAddress] = useState(() => localStorage.getItem('muse_last_address') || null);
  const [scanCountdown, setScanCountdown] = useState(0);
  const scanTimerRef = useRef(null);
  const [deviceBatteries, setDeviceBatteries] = useState({}); // address → battery % (null = cargando)
  const [connectedDevice, setConnectedDevice] = useState(null); // { name, address, battery } after connect

  // Device buffer + status (polled from /hardware/status)
  const [deviceBuffer, setDeviceBuffer] = useState(null); // { samples, fill_percent, duration_available }
  const [deviceAddress, setDeviceAddress] = useState(null);

  // Live EEG bands from WebSocket store
  const bands = useBrainStore(s => s.bands);
  const bandsDisplay = useBrainStore(s => s.bandsDisplay);
  const wsSource = useBrainStore(s => s.source);
  const wsCoherence = useBrainStore(s => s.coherence);
  
  // Calibración
  const [calibrationStep, setCalibrationStep] = useState(CALIBRATION_STEPS.IDLE);
  const [calibrationProgress, setCalibrationProgress] = useState(0);
  const [calibrationResults, setCalibrationResults] = useState(null);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [blinkCount, setBlinkCount] = useState(0);
  const [targetBlinks] = useState(5);
  const [baselineAlpha, setBaselineAlpha] = useState(null);
  const [relaxationPhase, setRelaxationPhase] = useState('open'); // 'open' o 'closed'
  const calibrationInterval = useRef(null);
  const confirmedBlinksRef = useRef(0); // Guardar blinks confirmados para evitar problemas de sincronización
  
  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSessionId, setRecordingSessionId] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef(null);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  
  // Audio context para sonidos
  const audioContextRef = useRef(null);

  // Función para reproducir un "pip"
  const playBeep = (frequency = 880, duration = 0.15) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.log('Audio not available');
    }
  };

  // ── Auto-recuperar estado al montar (página refrescada, backend sigue conectado) ──
  useEffect(() => {
    const recover = async () => {
      try {
        const res = await fetch(`${API_BASE}/hardware/status`);
        const data = await res.json();
        if (data.is_connected || data.is_streaming) {
          const info = data.device_info || {};
          const name = info.name || lastMuseAddress || 'Muse 2';
          const address = info.address || lastMuseAddress;
          const battery = info.battery_level ?? null;
          setConnectedDevice({ name, address, battery });
          if (address) setDeviceAddress(address);
          if (data.buffer) setDeviceBuffer(data.buffer);
          if (data.signal_quality) setSignalQuality(data.signal_quality);
          setStatus(data.is_streaming ? MUSE_STATUS.STREAMING : MUSE_STATUS.CONNECTED);
          console.log('[MuseControl] Sesión Muse recuperada al montar:', name, address);
        }
      } catch (e) {
        // Backend no disponible al montar — no hacer nada
      }
    };
    recover();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling del estado
  useEffect(() => {
    if (status !== MUSE_STATUS.STREAMING && status !== MUSE_STATUS.CALIBRATING) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/hardware/status`);
        const data = await res.json();
        if (data.signal_quality) setSignalQuality(data.signal_quality);
        if (data.buffer)        setDeviceBuffer(data.buffer);
        if (data.device_info?.address) setDeviceAddress(data.device_info.address);
      } catch (err) {
        console.error('Error fetching status:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    return () => {
      if (calibrationInterval.current) clearInterval(calibrationInterval.current);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (scanTimerRef.current) clearInterval(scanTimerRef.current);
    };
  }, []);
  
  // Recording timer
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      setRecordingTime(0);
    }
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [isRecording]);

  const scanDevices = async () => {
    // Primero: comprobar si el backend ya tiene el Muse conectado
    // (ocurre cuando se refresca la página pero el backend sigue corriendo)
    try {
      const statusRes = await fetch(`${API_BASE}/hardware/status`);
      const statusData = await statusRes.json();
      if (statusData.is_connected || statusData.is_streaming) {
        const info = statusData.device_info || {};
        const name = info.name || lastMuseAddress || 'Muse 2';
        const address = info.address || lastMuseAddress;
        const battery = info.battery_level ?? null;
        setConnectedDevice({ name, address, battery });
        if (address) setDeviceAddress(address);
        if (statusData.buffer) setDeviceBuffer(statusData.buffer);
        console.log('[MuseControl] Sesión ya activa en backend — saltando scan BLE:', name);
        if (statusData.is_streaming) {
          setStatus(MUSE_STATUS.STREAMING);
        } else {
          // Conectado pero sin stream activo — reiniciar stream automáticamente
          console.log('[MuseControl] CONNECTED pero no streaming — reactivando stream...');
          setStatus(MUSE_STATUS.CONNECTED);
          startStream();
        }
        return; // no hace falta escanear
      }
    } catch (_) {
      // Backend no responde — continuar con scan normal
    }

    const url = `${API_BASE}/hardware/devices`;
    console.group('[MuseControl] scanDevices');
    console.log('API_BASE:', API_BASE);
    console.log('URL:', url);
    console.log('lastMuseAddress (localStorage):', lastMuseAddress);

    setStatus(MUSE_STATUS.SCANNING);
    setError(null);

    // Countdown timer (backend scan takes ~10s)
    let remaining = 11;
    setScanCountdown(remaining);
    scanTimerRef.current = setInterval(() => {
      remaining -= 1;
      setScanCountdown(remaining);
      if (remaining <= 0) clearInterval(scanTimerRef.current);
    }, 1000);

    try {
      console.log('[MuseControl] → GET', url);
      const res = await fetch(url);
      console.log('[MuseControl] ← HTTP', res.status, res.statusText);

      clearInterval(scanTimerRef.current);
      setScanCountdown(0);

      const data = await res.json();
      console.log('[MuseControl] response body:', JSON.stringify(data, null, 2));

      if (data.devices?.length > 0) {
        console.log(`[MuseControl] ${data.devices.length} device(s) found:`);
        data.devices.forEach((d, i) => console.log(`  [${i}]`, d.name, '|', d.address, '| RSSI:', d.rssi));

        // Put last-used device first
        const sorted = [...data.devices].sort((a, b) => {
          if (a.address === lastMuseAddress) return -1;
          if (b.address === lastMuseAddress) return 1;
          return 0;
        });
        setDiscoveredDevices(sorted);
        setShowDeviceModal(true);
        setStatus(MUSE_STATUS.DISCONNECTED);
        // Batería: se lee en el backend durante connect(), no aquí,
        // para evitar que BleakClient y muselsl peleen por la conexión BLE.
      } else {
        console.warn('[MuseControl] No devices found. Full response:', data);
        setError('No se encontraron dispositivos Muse. ¿LED azul parpadeando?');
        setStatus(MUSE_STATUS.ERROR);
      }
    } catch (err) {
      clearInterval(scanTimerRef.current);
      setScanCountdown(0);
      console.error('[MuseControl] fetch error:', err.name, err.message);
      setError(`Error al escanear: ${err.message}`);
      setStatus(MUSE_STATUS.ERROR);
    } finally {
      console.groupEnd();
    }
  };

  const connectToDevice = async (address) => {
    const url = `${API_BASE}/hardware/connect/${encodeURIComponent(address)}`;
    console.group('[MuseControl] connectToDevice');
    console.log('address:', address);
    console.log('URL:', url);

    // Capture device name before closing modal
    const deviceMeta = discoveredDevices.find(d => d.address === address);
    setConnectedDevice({
      name: deviceMeta?.name || 'Muse',
      address,
      battery: null, // se actualiza desde la respuesta del connect
    });

    setShowDeviceModal(false);
    setStatus(MUSE_STATUS.SCANNING);
    setError(null);

    localStorage.setItem('muse_last_address', address);
    setLastMuseAddress(address);

    try {
      console.log('[MuseControl] → POST', url);
      const res = await fetch(url, { method: 'POST' });
      console.log('[MuseControl] ← HTTP', res.status, res.statusText);

      const data = await res.json();
      console.log('[MuseControl] connect response:', JSON.stringify(data, null, 2));

      if (data.status === 'success') {
        // Actualizar batería desde la respuesta del backend
        const batteryFromBackend = data.device?.battery_level ?? null;
        if (batteryFromBackend !== null) {
          setConnectedDevice(prev => prev ? { ...prev, battery: batteryFromBackend } : prev);
        }
        console.log('[MuseControl] Connected! Battery:', batteryFromBackend, '% — Starting stream...');
        setStatus(MUSE_STATUS.CONNECTED);
        startStream();
      } else {
        console.error('[MuseControl] Connect failed:', data.message);
        setError(data.message || 'No se pudo conectar al dispositivo');
        setStatus(MUSE_STATUS.ERROR);
      }
    } catch (err) {
      console.error('[MuseControl] connectToDevice fetch error:', err.name, err.message);
      setError(`Error al conectar: ${err.message}`);
      setStatus(MUSE_STATUS.ERROR);
    } finally {
      console.groupEnd();
    }
  };

  const startStream = async () => {
    console.log('[MuseControl] startStream → POST', `${API_BASE}/hardware/stream/start`);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/hardware/stream/start`, { method: 'POST' });
      const data = await res.json();
      console.log('[MuseControl] stream/start response:', data);
      
      if (data.status === 'success') {
        setStatus(MUSE_STATUS.STREAMING);
        // Activar modo muse inmediatamente — el brain 3D muestra señal real durante calibración
        try {
          await fetch(`${API_BASE}/set-mode/muse`, { method: 'POST' });
          console.log('[MuseControl] WS switched to live Muse EEG');
          if (onModeChange) onModeChange('muse');
        } catch (e) {
          console.warn('[MuseControl] set-mode/muse failed:', e.message);
        }
        startCalibration();
      } else {
        console.error('[MuseControl] stream/start failed:', data);
        setError(data.message || 'Error al iniciar streaming');
      }
    } catch (err) {
      console.error('[MuseControl] stream/start error:', err.message);
      setError('Error al iniciar streaming');
    }
  };

  const disconnect = async () => {
    if (calibrationInterval.current) clearInterval(calibrationInterval.current);
    try {
      await fetch(`${API_BASE}/hardware/disconnect`, { method: 'POST' });
      setStatus(MUSE_STATUS.DISCONNECTED);
      setSignalQuality(null);
      setCalibrationStep(CALIBRATION_STEPS.IDLE);
      setCalibrationResults(null);
      if (onModeChange) onModeChange('dataset');
    } catch (err) {
      console.error('Error disconnecting:', err);
    }
  };

  // ==================== CALIBRACIÓN ====================

  /**
   * logCal — fire-and-forget: envía un evento al backend para logging.
   * No bloquea — no afecta el timing de la calibración.
   */
  const logCal = (event, phase, data = {}) => {
    fetch(`${API_BASE}/hardware/calibration/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, phase, data }),
    }).catch(() => {}); // intencionalmente ignorado
  };
  
  const startCalibration = () => {
    setStatus(MUSE_STATUS.CALIBRATING);
    setCalibrationStep(CALIBRATION_STEPS.SIGNAL_CHECK);
    setCalibrationProgress(0);
    setCalibrationResults(null);
    setBlinkCount(0);
    setBaselineAlpha(null);
    setRelaxationPhase('open');
    logCal('calibration_start', 'init', {
      timestamp_iso: new Date().toISOString(),
      api_base: API_BASE,
    });
    runSignalCheck();
  };

  const runSignalCheck = async () => {
    setCalibrationStep(CALIBRATION_STEPS.SIGNAL_CHECK);
    let samples = [];
    let elapsed = 0;
    const duration = 3000;
    const warmupMs = 1000; // Discard first 1s — EMA buffer settling transient after BLE→LSL handoff
    
    calibrationInterval.current = setInterval(async () => {
      elapsed += 200;
      setCalibrationProgress((elapsed / duration) * 100);
      
      try {
        const res = await fetch(`${API_BASE}/hardware/calibration/snapshot`);
        const data = await res.json();
        if (data.status === 'success') {
          if (elapsed > warmupMs) samples.push(data); // warmup samples logged but not used for SQ
          setCurrentMetrics(data);
          logCal('signal_check_sample', 'signal_check', {
            elapsed_ms: elapsed,
            warmup: elapsed <= warmupMs,
            bands: data.bands,
            signal_quality: data.signal_quality,
            coherence: data.coherence,
          });
        }
      } catch (err) {}
      
      if (elapsed >= duration) {
        clearInterval(calibrationInterval.current);
        
        const avgQuality = samples.reduce((acc, s) => {
          const qualities = Object.values(s.signal_quality || {});
          return acc + (qualities.reduce((a, b) => a + b, 0) / qualities.length || 0);
        }, 0) / (samples.length || 1);
        
        // Contar electrodos con buen contacto (quality >= 0.8) en la última muestra
        const lastQuality = samples.at(-1)?.signal_quality || {};
        const goodElectrodes = Object.values(lastQuality).filter(q => q >= 0.8).length;
        
        // Verificar rango fisiológico: gamma > 5000 µV² = ruido de línea (sin casco)
        const lastBands = samples.at(-1)?.bands || {};
        const gammaPhysiological = (lastBands.gamma || 0) < 5000;
        const deltaPhysiological = (lastBands.delta || 0) < 10000;
        const bandsOk = gammaPhysiological && deltaPhysiological;
        
        const passed = avgQuality >= 0.5 && goodElectrodes >= 2 && bandsOk;
        
        logCal('signal_check_result', 'signal_check', {
          avgQuality: parseFloat(avgQuality.toFixed(4)),
          samplesCollected: samples.length,
          passed,
          threshold: 0.5,
          goodElectrodes,
          bandsOk,
          gamma_last: parseFloat((lastBands.gamma || 0).toFixed(1)),
          delta_last: parseFloat((lastBands.delta || 0).toFixed(1)),
          perElectrode_last: lastQuality,
        });

        if (!passed) {
          let reason = 'Señal débil.';
          if (avgQuality < 0.5) reason = `Calidad de señal muy baja (${(avgQuality * 100).toFixed(0)}%). Ajusta la diadema.`;
          else if (goodElectrodes < 2) reason = `Solo ${goodElectrodes} electrodo(s) con buen contacto. Necesitas al menos 2. Humedece los sensores.`;
          else if (!bandsOk) reason = 'Señal fuera de rango fisiológico — ¿el casco está puesto?';
          setCalibrationStep(CALIBRATION_STEPS.FAILED);
          setCalibrationResults({ passed: false, reason });
          setStatus(MUSE_STATUS.STREAMING);
          logCal('calibration_failed', 'result', { reason, avgQuality, goodElectrodes, bandsOk });
        } else {
          setTimeout(() => runBlinkTest(), 500);
        }
      }
    }, 200);
  };

  const runBlinkTest = async () => {
    setCalibrationStep(CALIBRATION_STEPS.BLINK_TEST);
    setCalibrationProgress(0);
    setBlinkCount(0);
    confirmedBlinksRef.current = 0; // Reset ref
    let elapsed = 0;
    const duration = 20000; // 20 segundos para pestañear 5 veces
    let cooldownUntil = 0; // Para evitar contar el mismo parpadeo múltiples veces

    logCal('blink_test_start', 'blink_test', {
      target_blinks: targetBlinks,
      duration_ms: duration,
      poll_interval_ms: 300,
    });

    // Poll up to 3s for SQ to recover >0.7 (blink artifacts clear within ~500ms-2s)
    // Non-blocking: if it never recovers, we log a warning and proceed anyway.
    const sqRecoveryThenRelax = async () => {
      for (let attempt = 0; attempt < 10; attempt++) {
        await new Promise(r => setTimeout(r, 300));
        try {
          const res = await fetch(`${API_BASE}/hardware/calibration/snapshot`);
          const snap = await res.json();
          if (snap.status === 'success') {
            const sqVals = Object.values(snap.signal_quality || {});
            const avgSQ = sqVals.length ? sqVals.reduce((a, b) => a + b, 0) / sqVals.length : 0;
            logCal('post_blink_sq_recovery', 'blink_test', {
              attempt, waited_ms: (attempt + 1) * 300,
              avgSQ: parseFloat(avgSQ.toFixed(3)),
              recovered: avgSQ >= 0.7,
            });
            if (avgSQ >= 0.7) break;
          }
        } catch (_) {}
      }
      runRelaxationTest();
    };
    
    calibrationInterval.current = setInterval(async () => {
      elapsed += 300;
      setCalibrationProgress((elapsed / duration) * 100);
      
      const currentTime = Date.now();
      
      // Si estamos en cooldown, no procesar
      if (currentTime < cooldownUntil) {
        return;
      }
      
      try {
        const res = await fetch(`${API_BASE}/hardware/calibration/blinks`);
        const data = await res.json();

        // Log every poll — useful to see what the signal looked like when NOT blinking
        logCal('blink_poll', 'blink_test', {
          elapsed_ms: elapsed,
          blink_count: data.blink_count,
          avg_amplitude: data.avg_amplitude,
          max_amplitude: data.max_amplitude,
          threshold: data.threshold,
          baseline: data.baseline,
          noise_level: data.noise_level,
          blink_times: data.blink_times,
          signal_quality: data.signal_quality,
        });

        if (data.status === 'success' && data.blink_count > 0) {
          // NOTE: No quality gate here. Blinks ARE artifacts that temporarily
          // crash signal quality (400-800µV spike → SQ drops to 0.3).
          // The backend already validates the blink pattern (amplitude,
          // prominence, physiological timing). If the backend says it's
          // a blink, we count it.
          // Detectamos parpadeo(s) en esta ventana
          // Solo contamos 1 por detección (las ventanas se solapan)
          confirmedBlinksRef.current += 1;
          
          // Cooldown de 800ms para evitar contar el mismo parpadeo
          cooldownUntil = currentTime + 800;
          
          // Sonido de confirmación
          playBeep(660, 0.08);
          
          // Actualizar UI
          const newCount = Math.min(confirmedBlinksRef.current, targetBlinks);
          setBlinkCount(newCount);
          
          console.log(`👁️ Blink detected! Total: ${confirmedBlinksRef.current}, Amplitude: ${data.avg_amplitude?.toFixed(1)}µV`);

          logCal('blink_detected', 'blink_test', {
            elapsed_ms: elapsed,
            confirmed_total: confirmedBlinksRef.current,
            avg_amplitude: data.avg_amplitude,
            max_amplitude: data.max_amplitude,
            threshold: data.threshold,
          });
        }
        setCurrentMetrics(data);
      } catch (err) {
        console.error('Error detecting blinks:', err);
      }
      
      // Éxito si detectamos los 5 parpadeos
      if (confirmedBlinksRef.current >= targetBlinks) {
        clearInterval(calibrationInterval.current);
        playBeep(880, 0.2); // Sonido de éxito
        logCal('blink_test_result', 'blink_test', {
          outcome: 'success_target_reached',
          confirmed_blinks: confirmedBlinksRef.current,
          target: targetBlinks,
          elapsed_ms: elapsed,
        });
        sqRecoveryThenRelax();
        return;
      }
      
      // Timeout
      if (elapsed >= duration) {
        clearInterval(calibrationInterval.current);
        if (confirmedBlinksRef.current >= 3) {
          // Al menos 3 parpadeos detectados, continuar
          playBeep(880, 0.2);
          logCal('blink_test_result', 'blink_test', {
            outcome: 'success_partial',
            confirmed_blinks: confirmedBlinksRef.current,
            target: targetBlinks,
            elapsed_ms: elapsed,
          });
          sqRecoveryThenRelax();
        } else {
          playBeep(330, 0.3); // Sonido de fallo
          setCalibrationStep(CALIBRATION_STEPS.FAILED);
          setCalibrationResults({ 
            passed: false, 
            reason: `Solo se detectaron ${confirmedBlinksRef.current} parpadeos de ${targetBlinks}. Pestañea con más fuerza y espera el "pip" de confirmación.`,
            blinksDetected: confirmedBlinksRef.current
          });
          setStatus(MUSE_STATUS.STREAMING);
          logCal('blink_test_result', 'blink_test', {
            outcome: 'failed',
            confirmed_blinks: confirmedBlinksRef.current,
            target: targetBlinks,
            elapsed_ms: elapsed,
          });
          logCal('calibration_failed', 'result', {
            reason: 'insufficient_blinks',
            confirmed_blinks: confirmedBlinksRef.current,
            target: targetBlinks,
            passed: false,
          });
        }
      }
    }, 300); // Polling cada 300ms
  };

  const runRelaxationTest = async () => {
    // Primero: capturar baseline alpha con OJOS ABIERTOS (3 segundos)
    setCalibrationStep(CALIBRATION_STEPS.RELAXATION);
    setCalibrationProgress(0);
    let eyesOpenSamples = [];
    let eyesClosedSamples = [];
    let elapsed = 0;
    const eyesOpenDuration = 20000;  // 20s ojos abiertos: 2s warmup + 18s útiles
    const eyesClosedDuration = 20000; // 20s ojos cerrados: 2s warmup + 18s útiles
    const totalDuration = eyesOpenDuration + eyesClosedDuration;
    let phase = 'open'; // 'open' -> 'closed'
    let phaseBeeped = false;
    
    // Mensaje inicial
    setRelaxationPhase?.('open');

    logCal('relaxation_start', 'eyes_open', {
      eyes_open_duration_ms: eyesOpenDuration,
      eyes_closed_duration_ms: eyesClosedDuration,
      warmup_open_skip_ms: 2000,
      warmup_closed_skip_ms: 2200,
      poll_interval_ms: 200,
    });
    
    calibrationInterval.current = setInterval(async () => {
      elapsed += 200;
      setCalibrationProgress((elapsed / totalDuration) * 100);
      
      // Cambiar de fase: ojos abiertos -> ojos cerrados
      if (phase === 'open' && elapsed >= eyesOpenDuration) {
        phase = 'closed';
        playBeep(440, 0.3); // Pip grave para cerrar ojos
        setRelaxationPhase?.('closed');
        logCal('phase_changed', 'transition', {
          from: 'eyes_open',
          to: 'eyes_closed',
          elapsed_ms: elapsed,
          open_samples_collected: eyesOpenSamples.length,
          open_alpha_values: eyesOpenSamples.map(s => s.bands?.alpha),
        });
      }
      
      // Pip antes de terminar para abrir ojos
      if (phase === 'closed' && !phaseBeeped && elapsed >= totalDuration - 500) {
        playBeep(1200, 0.25); // Pip agudo para abrir ojos
        phaseBeeped = true;
      }
      
      try {
        const res = await fetch(`${API_BASE}/hardware/calibration/snapshot`);
        const data = await res.json();
        if (data.status === 'success') {
          // EOG filter: skip blink-contaminated windows for alpha calculation.
          // Blinks propagate via volume conduction to TP9/TP10 and inflate alpha.
          // The backend detects blinks using frontal AF7/AF8 channels.
          const isClean = !data.blink_contaminated;
          
          if (isClean) {
            if (phase === 'open') {
              eyesOpenSamples.push(data);
            } else {
              eyesClosedSamples.push(data);
            }
          } else {
            console.log(`⚡ EOG rejected [${phase}]: alpha=${data.bands?.alpha?.toFixed(1)}µV² (peak=${data.eog?.frontal_peak_uv?.toFixed(0)}µV)`);
          }
          
          setCurrentMetrics(data);
          console.log(`📊 Alpha snapshot [${phase}]${isClean ? '' : ' ⚡BLINK'}: alpha=${data.bands?.alpha?.toFixed(1)}, beta=${data.bands?.beta?.toFixed(1)}`);

          // Log every sample with full bands + quality + EOG status
          logCal(isClean ? 'relaxation_sample' : 'relaxation_sample_eog_rejected',
            phase === 'open' ? 'eyes_open' : 'eyes_closed', {
            elapsed_ms: elapsed,
            phase,
            bands: data.bands,
            coherence: data.coherence,
            signal_quality: data.signal_quality,
            blink_contaminated: data.blink_contaminated,
            eog: data.eog,
          });
        }
      } catch (err) {
        console.error('Error fetching snapshot:', err);
      }
      
      if (elapsed >= totalDuration) {
        clearInterval(calibrationInterval.current);
        
        // --- WARMUP OJOS ABIERTOS ---
        // Descartar primeros 2000ms: artefactos de transición desde el blink test.
        const warmupOpen = Math.ceil(2000 / 200); // 10 muestras

        // --- WARMUP OJOS CERRADOS ---
        // La ventana Welch usa los últimos 2s del buffer.
        // Para que la ventana esté ENTERAMENTE en eyes_closed necesitamos
        // descartar los primeros 2200ms tras la transición (margen para Welch).
        const warmupClosed = Math.ceil(2200 / 200); // 11 muestras

        // Con 20s por fase y los warmups, quedan ~18s útiles por fase (~90 muestras).
        // Se usa TODA la ventana estable — sin límite artificial de N muestras.
        const openToUse   = eyesOpenSamples.slice(warmupOpen);
        const closedToUse = eyesClosedSamples.slice(warmupClosed);
        
        // Median: robust estimator for bimodal distributions.
        // Even with EOG filtering, residual blink-adjacent windows may inflate
        // the mean. Median captures the center of the clean distribution.
        const median = (arr, key) => {
          const vals = arr.map(s => s.bands?.[key] || 0).sort((a, b) => a - b);
          if (vals.length === 0) return 0;
          const mid = Math.floor(vals.length / 2);
          return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
        };
        
        // Trimmed mean still used for non-alpha bands (less affected by EOG)
        const trimmedMean = (arr, key, trimTop = 0.2) => {
          const vals = arr.map(s => s.bands?.[key] || 0).sort((a, b) => a - b);
          const cutoff = Math.floor(vals.length * (1 - trimTop));
          const trimmed = vals.slice(0, cutoff);
          return trimmed.reduce((a, b) => a + b, 0) / (trimmed.length || 1);
        };
        
        // Alpha uses MEDIAN (robust to residual EOG), other bands use trimmedMean
        const avgAlphaOpen   = median(openToUse, 'alpha');
        const avgAlphaClosed = median(closedToUse, 'alpha');
        
        console.log(`📊 Alpha after warmup trim: open=${avgAlphaOpen.toFixed(1)} (${openToUse.length} samples, skipped ${warmupOpen}), closed=${avgAlphaClosed.toFixed(1)} (${closedToUse.length} samples, skipped ${warmupClosed})`);

        // Log complete computed analysis — the key diagnostic data
        logCal('relaxation_computed', 'analysis', {
          eyes_open: {
            raw_samples_count: eyesOpenSamples.length,
            warmup_skipped: warmupOpen,
            used_samples_count: openToUse.length,
            alpha_all_values: eyesOpenSamples.map(s => s.bands?.alpha),
            alpha_used_values: openToUse.map(s => s.bands?.alpha),
            avgAlpha: parseFloat(avgAlphaOpen.toFixed(3)),
            avgBeta: parseFloat(trimmedMean(openToUse, 'beta').toFixed(3)),
            avgTheta: parseFloat(trimmedMean(openToUse, 'theta').toFixed(3)),
            avgDelta: parseFloat(trimmedMean(openToUse, 'delta').toFixed(3)),
            avgGamma: parseFloat(trimmedMean(openToUse, 'gamma').toFixed(3)),
          },
          eyes_closed: {
            raw_samples_count: eyesClosedSamples.length,
            warmup_skipped: warmupClosed,
            used_samples_count: closedToUse.length,
            alpha_all_values: eyesClosedSamples.map(s => s.bands?.alpha),
            alpha_used_values: closedToUse.map(s => s.bands?.alpha),
            avgAlpha: parseFloat(avgAlphaClosed.toFixed(3)),
            avgBeta: parseFloat(trimmedMean(closedToUse, 'beta').toFixed(3)),
            avgTheta: parseFloat(trimmedMean(closedToUse, 'theta').toFixed(3)),
            avgDelta: parseFloat(trimmedMean(closedToUse, 'delta').toFixed(3)),
            avgGamma: parseFloat(trimmedMean(closedToUse, 'gamma').toFixed(3)),
          },
          alpha_ratio: parseFloat((avgAlphaClosed / (avgAlphaOpen || 1)).toFixed(4)),
          threshold: 0.5,
          eog_filtering: {
            open_total_snapshots: eyesOpenSamples.length + (eyesOpenDuration / 200 - eyesOpenSamples.length),
            open_clean_samples: eyesOpenSamples.length,
            closed_clean_samples: eyesClosedSamples.length,
            estimator: 'median',  // Changed from trimmedMean — robust to residual EOG
          },
        });
        
        // Coherencia promedio de las muestras con ojos cerrados (señal real > 0.8)
        const avgCoherence = closedToUse.reduce((acc, s) => acc + (s.coherence || 0), 0) / (closedToUse.length || 1);
        
        setBaselineAlpha(avgAlphaOpen);
        evaluateCalibration(avgAlphaOpen, avgAlphaClosed, avgCoherence);
      }
    }, 200);
  };

  const evaluateCalibration = (alphaOpen, alphaClosed, avgCoherence = 0) => {
    // La calibración es exitosa si:
    // 1. Detectamos suficientes parpadeos (ya validado antes)
    // 2. Alpha con ojos cerrados es MAYOR que con ojos abiertos (fisiología normal)
    // 3. Coherencia > 0.75 (señal cerebral real vs ruido — sin casco ~0.5-0.65)
    
    const detectedBlinks = confirmedBlinksRef.current;
    const alphaRatio = alphaClosed / (alphaOpen || 1);
    // Alpha DEBE aumentar al cerrar ojos (Berger effect, 1929).
    // ratio > 1.0 = alpha subió. Mínimo aceptable: 1.05 (5% aumento).
    // Ratio < 1.0 con ojos cerrados = la señal no está captando actividad cortical real.
    const alphaOk = alphaRatio >= 1.05;
    const alphaIncreased = alphaRatio > 1.15;
    const coherenceOk = avgCoherence > 0.75;
    const passed = detectedBlinks >= 3 && alphaOk && coherenceOk;
    
    console.log(`📊 Calibration evaluation: blinks=${detectedBlinks}, alphaOpen=${alphaOpen.toFixed(2)}, alphaClosed=${alphaClosed.toFixed(2)}, ratio=${alphaRatio.toFixed(2)}, coherence=${avgCoherence.toFixed(3)}`);
    
    let reason;
    if (passed) {
      if (alphaIncreased) {
        reason = `✓ Calibración exitosa: Alpha aumentó ${((alphaRatio - 1) * 100).toFixed(0)}% al cerrar ojos (coherencia: ${avgCoherence.toFixed(2)})`;
      } else {
        reason = `✓ Calibración exitosa: Efecto Berger detectado, ratio ${alphaRatio.toFixed(2)} (coherencia: ${avgCoherence.toFixed(2)})`;
      }
    } else if (detectedBlinks < 3) {
      reason = `✕ Pocos parpadeos detectados (${detectedBlinks}/${targetBlinks}). Asegúrate de pestañear fuerte cuando el test lo pida.`;
    } else if (!alphaOk) {
      reason = `✕ Alpha no aumentó al cerrar ojos (ratio: ${alphaRatio.toFixed(2)}, necesario ≥1.05). ¿Cerraste los ojos durante la fase 'ojos cerrados'? Verifica que los sensores frontales tengan buen contacto.`;
    } else if (!coherenceOk) {
      reason = `✕ Coherencia hemisférica baja (${avgCoherence.toFixed(2)}). ¿El casco está bien puesto? Humedece los sensores.`;
    } else {
      reason = '✕ No se pudo completar la calibración';
    }

    const evalData = {
      passed,
      blinks_detected: detectedBlinks,
      target_blinks: targetBlinks,
      alpha_open: parseFloat(alphaOpen.toFixed(3)),
      alpha_closed: parseFloat(alphaClosed.toFixed(3)),
      alpha_ratio: parseFloat(alphaRatio.toFixed(4)),
      alpha_ok: alphaOk,
      alpha_increased: alphaIncreased,
      coherence_avg: parseFloat(avgCoherence.toFixed(4)),
      coherence_ok: coherenceOk,
      threshold_ratio: 1.05,
      threshold_coherence: 0.75,
      reason,
    };

    logCal('calibration_evaluated', 'result', evalData);
    logCal(passed ? 'calibration_complete' : 'calibration_failed', 'final', evalData);
    
    setCalibrationResults({
      passed,
      blinksDetected: detectedBlinks,
      targetBlinks: targetBlinks,
      alphaOpen: alphaOpen.toFixed(1),
      alphaClosed: alphaClosed.toFixed(1),
      alphaRatio: alphaRatio.toFixed(2),
      baselineAlpha: alphaOpen.toFixed(1),
      reason
    });
    
    setCalibrationStep(CALIBRATION_STEPS.COMPLETE);
    setStatus(MUSE_STATUS.STREAMING);
    
    if (passed) {
      setTimeout(async () => {
        await fetch(`${API_BASE}/set-mode/muse`, { method: 'POST' });
        if (onModeChange) onModeChange('muse');
      }, 1000);
    }
  };

  const skipCalibration = async () => {
    if (calibrationInterval.current) clearInterval(calibrationInterval.current);
    setCalibrationStep(CALIBRATION_STEPS.IDLE);
    setStatus(MUSE_STATUS.STREAMING);
    await fetch(`${API_BASE}/set-mode/muse`, { method: 'POST' });
    if (onModeChange) onModeChange('muse');
  };

  // ==================== GRABACIÓN ====================
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const startRecording = async () => {
    try {
      const res = await fetch(`${API_BASE}/recording/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: sessionName || `Sesión ${new Date().toLocaleString()}`,
          notes: sessionNotes,
          tags: ''
        })
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        setIsRecording(true);
        setRecordingSessionId(data.session_id);
        setShowRecordingModal(false);
        setSessionName('');
        setSessionNotes('');
        useBrainStore.getState().setSessionActive(true);
      } else {
        setError(data.message || 'Error al iniciar grabación');
      }
    } catch (err) {
      setError('Error al iniciar grabación');
    }
  };
  
  const stopRecording = async () => {
    try {
      const res = await fetch(`${API_BASE}/recording/stop`, { method: 'POST' });
      const data = await res.json();
      
      if (data.status === 'success') {
        setIsRecording(false);
        setRecordingSessionId(null);
        useBrainStore.getState().setSessionActive(false);
        // Mostrar resumen breve
        console.log('Recording saved:', data.session);
      }
    } catch (err) {
      setError('Error al detener grabación');
    }
  };
  
  const addMarker = async (label) => {
    if (!isRecording) return;
    try {
      await fetch(`${API_BASE}/recording/marker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, event_type: 'marker' })
      });
    } catch (err) {
      console.error('Error adding marker:', err);
    }
  };

  // ==================== UI ====================

  const getStatusColor = () => {
    switch (status) {
      case MUSE_STATUS.STREAMING: return '#00ff88';
      case MUSE_STATUS.CALIBRATING: return '#ffaa00';
      case MUSE_STATUS.CONNECTED: return '#00aaff';
      case MUSE_STATUS.SCANNING: return '#ffaa00';
      case MUSE_STATUS.ERROR: return '#ff4444';
      default: return '#666';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case MUSE_STATUS.STREAMING: return '● LIVE EEG';
      case MUSE_STATUS.CALIBRATING: return '◐ Calibrando...';
      case MUSE_STATUS.CONNECTED: return '● Conectado';
      case MUSE_STATUS.SCANNING: return '○ Buscando...';
      case MUSE_STATUS.ERROR: return '● Error';
      default: return '○ Desconectado';
    }
  };

  const getCalibrationInstruction = () => {
    // Calcular tiempo restante basado en progreso
    const getTimeRemaining = (durationMs) => {
      const remaining = Math.ceil((durationMs * (100 - calibrationProgress) / 100) / 1000);
      return remaining;
    };
    
    switch (calibrationStep) {
      case CALIBRATION_STEPS.SIGNAL_CHECK:
        return { 
          emoji: '📡', 
          text: 'Verificando señal...', 
          subtext: 'Quédate quieto',
          timeRemaining: getTimeRemaining(3000),
          showTimer: true
        };
      case CALIBRATION_STEPS.BLINK_TEST:
        return { 
          emoji: '👁️', 
          text: 'PESTAÑEA FUERTE', 
          subtext: `${blinkCount} de ${targetBlinks} parpadeos detectados`,
          showBlinks: true
        };
      case CALIBRATION_STEPS.RELAXATION:
        if (relaxationPhase === 'open') {
          // calibrationProgress 0→50 maps to 0→100% of open phase
          const openProgress = Math.min(calibrationProgress * 2, 100);
          return { 
            emoji: '👀', 
            text: 'MANTÉN OJOS ABIERTOS', 
            subtext: 'Midiendo baseline... escucha el pip',
            timeRemaining: Math.ceil((20000 * (100 - openProgress) / 100) / 1000),
            showTimer: true
          };
        } else {
          // calibrationProgress 50→100 maps to 0→100% of closed phase
          const closedProgress = Math.min((calibrationProgress - 50) * 2, 100);
          return { 
            emoji: '😌', 
            text: 'CIERRA LOS OJOS', 
            subtext: 'Relájate... pip para abrirlos',
            timeRemaining: Math.ceil((20000 * (100 - closedProgress) / 100) / 1000),
            showTimer: true
          };
        }
      default:
        return null;
    }
  };

  // ── Detecta señal plana (todos los valores iguales = waiting_data) ──────────
  const isWaitingData = bands &&
    Object.values(bands).every(v => Math.abs(v - 0.2) < 0.001);

  const renderDeviceStatus = () => {
    const noFlow = deviceBuffer?.samples === 0 || isWaitingData;
    const fillPct = deviceBuffer?.fill_percent ?? 0;
    const shortAddr = deviceAddress
      ? deviceAddress.replace(/:/g, '').slice(-6).toUpperCase()
      : '——';

    return (
      <div style={{
        marginBottom: '10px',
        padding: '10px 12px',
        background: noFlow ? 'rgba(255,100,0,0.08)' : 'rgba(0,255,136,0.05)',
        border: `1px solid ${noFlow ? 'rgba(255,100,0,0.3)' : 'rgba(0,255,136,0.15)'}`,
        borderRadius: '8px',
        fontSize: '0.65rem',
        fontFamily: 'monospace',
      }}>
        {/* Fila superior: ID + estado */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '7px', height: '7px', borderRadius: '50%',
              background: noFlow ? '#ff6400' : '#00ff88',
              boxShadow: `0 0 8px ${noFlow ? '#ff640066' : '#00ff8866'}`,
              animation: noFlow ? 'none' : 'pulse 2s infinite',
            }} />
            <span style={{ opacity: 0.8, color: '#cceeff' }}>{connectedDevice?.name || 'Muse'}</span>
            {connectedDevice?.battery != null && (
              <span style={{
                fontSize: '0.6rem', fontWeight: 'bold',
                color: connectedDevice.battery > 40 ? '#44ff88' : connectedDevice.battery > 15 ? '#ffaa00' : '#ff4444'
              }}>
                {connectedDevice.battery > 80 ? '🔋' : connectedDevice.battery > 40 ? '🪫' : '⚡'}
                {' '}{connectedDevice.battery}%
              </span>
            )}
            <span style={{ opacity: 0.3 }}>#{shortAddr}</span>
          </div>
          <span style={{ color: noFlow ? '#ff8844' : '#00ff88', fontWeight: 'bold' }}>
            {noFlow ? 'SIN DATOS' : 'STREAMING'}
          </span>
        </div>

        {/* Buffer fill */}
        <div style={{ marginBottom: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
            <span style={{ opacity: 0.4 }}>Buffer EEG</span>
            <span style={{ color: fillPct > 10 ? '#00ff88' : '#ff6400' }}>
              {deviceBuffer ? `${deviceBuffer.samples} muestras (${fillPct.toFixed(0)}%)` : '—'}
            </span>
          </div>
          <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              width: `${fillPct}%`, height: '100%',
              background: fillPct > 10 ? '#00ff88' : '#ff6400',
              transition: 'width 0.5s',
            }} />
          </div>
        </div>

        {/* Warning si no hay datos */}
        {noFlow && (
          <div style={{
            marginTop: '7px', padding: '7px 9px',
            background: 'rgba(255,80,0,0.12)',
            border: '1px solid rgba(255,80,0,0.25)',
            borderRadius: '6px',
            lineHeight: '1.6',
          }}>
            <div style={{ color: '#ff8844', fontWeight: 'bold', marginBottom: '4px' }}>
              ⚠ EEG no recibido
            </div>
            <div style={{ opacity: 0.65 }}>Posibles causas:</div>
            <div style={{ opacity: 0.55, paddingLeft: '8px' }}>
              • Batería baja — recarga el Muse<br />
              • Electrodos sin contacto — ajusta la diadema<br />
              • Stream LSL roto — desconecta y reconecta
            </div>
            <button
              onClick={async () => {
                // Check if backend is still connected — avoid disconnecting a live device
                try {
                  const res = await fetch(`${API_BASE}/hardware/status`);
                  const data = await res.json();
                  if (data.is_connected || data.is_streaming) {
                    // Backend still has the device — just recover the UI state
                    setError(null);
                    scanDevices();
                    return;
                  }
                } catch (_) {}
                // Backend lost device — full disconnect + rescan
                await disconnect();
                setTimeout(() => scanDevices(), 300);
              }}
              style={{
                marginTop: '8px', width: '100%', padding: '6px',
                background: 'rgba(255,80,0,0.2)',
                border: '1px solid rgba(255,80,0,0.4)',
                borderRadius: '5px', color: '#ff8844',
                fontSize: '0.62rem', cursor: 'pointer', fontFamily: 'monospace',
              }}
            >
              ↺ Desconectar y reconectar
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSignalQuality = () => {
    if (!signalQuality) return null;
    const channels = ['TP9', 'AF7', 'AF8', 'TP10'];
    
    return (
      <div style={{ marginTop: '10px' }}>
        <div style={{ fontSize: '0.65rem', opacity: 0.6, marginBottom: '5px' }}>ELECTRODOS</div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          {channels.map(ch => {
            const quality = signalQuality[ch] || 0;
            const color = quality > 0.7 ? '#00ff88' : quality > 0.4 ? '#ffaa00' : '#ff4444';
            return (
              <div key={ch} style={{ textAlign: 'center' }}>
                <div style={{ width: '30px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ width: `${quality * 100}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: '0.55rem', opacity: 0.5, marginTop: '2px' }}>{ch}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLiveBands = () => {
    const source = bandsDisplay || bands;
    if (!source) return null;

    const bandDefs = [
      { key: 'delta', label: 'δ Delta',  color: '#6366f1', desc: '1-4 Hz' },
      { key: 'theta', label: 'θ Theta',  color: '#8b5cf6', desc: '4-8 Hz' },
      { key: 'alpha', label: 'α Alpha',  color: '#00ff88', desc: '8-13 Hz' },
      { key: 'beta',  label: 'β Beta',   color: '#00aaff', desc: '13-30 Hz' },
      { key: 'gamma', label: 'γ Gamma',  color: '#ff6b35', desc: '30-50 Hz' },
    ];

    return (
      <div style={{
        marginTop: '12px',
        padding: '10px 12px',
        background: 'rgba(0,0,0,0.25)',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '0.58rem', opacity: 0.45, letterSpacing: '0.18em' }}>BANDAS EEG EN VIVO</div>
          {wsSource && (
            <div style={{ fontSize: '0.55rem', opacity: 0.4, color: '#00ff88' }}>
              {wsSource}
            </div>
          )}
        </div>

        {bandDefs.map(({ key, label, color, desc }) => {
          const raw = source[key] ?? 0;
          // bandsDisplay values are 0-1 normalized; raw bands may be larger
          const pct = Math.min(raw * 100, 100);
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px' }}>
              <span style={{ width: '52px', fontSize: '0.6rem', color, opacity: 0.85, whiteSpace: 'nowrap' }}>{label}</span>
              <div style={{ flex: 1, height: '5px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: color,
                  borderRadius: '3px',
                  transition: 'width 0.25s ease',
                  boxShadow: `0 0 6px ${color}66`,
                }} />
              </div>
              <span style={{ width: '30px', fontSize: '0.58rem', color, textAlign: 'right', opacity: 0.75 }}>
                {pct.toFixed(0)}%
              </span>
            </div>
          );
        })}

        {(wsCoherence !== undefined && wsCoherence !== null) && (
          <div style={{ marginTop: '7px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.57rem', opacity: 0.4 }}>Coherencia hemisférica</span>
            <span style={{ fontSize: '0.57rem', color: wsCoherence > 0.5 ? '#00ff88' : '#ffaa00', opacity: 0.8 }}>
              {(wsCoherence * 100).toFixed(0)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderCalibration = () => {
    const instruction = getCalibrationInstruction();
    
    if (!instruction && calibrationStep !== CALIBRATION_STEPS.COMPLETE && calibrationStep !== CALIBRATION_STEPS.FAILED) {
      return null;
    }
    
    return (
      <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '15px', marginTop: '10px', textAlign: 'center' }}>
        {instruction && (
          <>
            <div style={{ fontSize: '2rem', marginBottom: '5px' }}>{instruction.emoji}</div>
            <div style={{ fontSize: '1rem', fontWeight: 'bold', color: calibrationStep === CALIBRATION_STEPS.EYES_CLOSED ? '#ffaa00' : '#00aaff', marginBottom: '5px' }}>
              {instruction.text}
            </div>
            
            {/* Contador de segundos restantes (para tests con timer) */}
            {instruction.showTimer && (
              <div style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                color: calibrationStep === CALIBRATION_STEPS.RELAXATION ? '#ffaa00' : '#00aaff',
                marginBottom: '5px',
                fontFamily: 'monospace',
                textShadow: `0 0 20px ${calibrationStep === CALIBRATION_STEPS.RELAXATION ? '#ffaa0050' : '#00aaff50'}`
              }}>
                {instruction.timeRemaining}s
              </div>
            )}
            
            {/* Contador de parpadeos */}
            {instruction.showBlinks && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '10px' }}>
                  {[...Array(targetBlinks)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: i < blinkCount ? '#00ff88' : 'rgba(255,255,255,0.2)',
                        border: `2px solid ${i < blinkCount ? '#00ff88' : 'rgba(255,255,255,0.3)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        transition: 'all 0.3s',
                        transform: i < blinkCount ? 'scale(1.1)' : 'scale(1)'
                      }}
                    >
                      {i < blinkCount ? '✓' : ''}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '1.5rem', color: '#00ff88', fontWeight: 'bold' }}>
                  {blinkCount} / {targetBlinks}
                </div>
              </div>
            )}
            
            <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: '10px' }}>{instruction.subtext}</div>
            
            {/* Calidad de electrodos durante calibración — siempre visible */}
            {currentMetrics?.signal_quality && (() => {
              const sq = currentMetrics.signal_quality;
              const channels = ['TP9', 'AF7', 'AF8', 'TP10'];
              
              // Durante relajación, alpha viene de TP9/TP10 (posteriores).
              // AF7/AF8 no importan para esta fase — no mostrar falsa alarma.
              const isRelaxationPhase = calibrationStep === CALIBRATION_STEPS.RELAXATION;
              const criticalChannels = isRelaxationPhase ? ['TP9', 'TP10'] : channels;
              const badChannels = criticalChannels.filter(ch => (sq[ch] || 0) < 0.5);
              
              return (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '4px' }}>
                    {channels.map(ch => {
                      const q = sq[ch] || 0;
                      const isCritical = criticalChannels.includes(ch);
                      // Canales no críticos en esta fase se muestran más atenuados
                      const color = !isCritical
                        ? 'rgba(255,255,255,0.25)'
                        : q > 0.7 ? '#00ff88' : q > 0.4 ? '#ffaa00' : '#ff4444';
                      return (
                        <div key={ch} style={{ textAlign: 'center', opacity: isCritical ? 1 : 0.4 }}>
                          <div style={{ width: '28px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{ width: `${q * 100}%`, height: '100%', background: color, transition: 'width 0.5s' }} />
                          </div>
                          <div style={{ fontSize: '0.5rem', marginTop: '2px', color }}>{ch}</div>
                        </div>
                      );
                    })}
                  </div>
                  {isRelaxationPhase && (
                    <div style={{ fontSize: '0.48rem', opacity: 0.35, marginBottom: '3px' }}>
                      Alpha medido desde TP9 + TP10
                    </div>
                  )}
                  {badChannels.length > 0 && (
                    <div style={{ fontSize: '0.6rem', color: '#ff6644', background: 'rgba(255,60,0,0.12)', borderRadius: '4px', padding: '3px 8px', display: 'inline-block' }}>
                      ⚠️ {badChannels.join(', ')} — señal débil, ajusta la diadema
                    </div>
                  )}
                </div>
              );
            })()}
            
            <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden', marginBottom: '10px' }}>
              <div style={{ width: `${calibrationStep === CALIBRATION_STEPS.BLINK_TEST ? (blinkCount / targetBlinks) * 100 : calibrationProgress}%`, height: '100%', background: calibrationStep === CALIBRATION_STEPS.RELAXATION ? '#ffaa00' : '#00aaff', transition: 'width 0.2s' }} />
            </div>
            
            {currentMetrics?.bands && calibrationStep === CALIBRATION_STEPS.RELAXATION && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
                <div style={{ 
                  fontSize: '0.75rem', 
                  padding: '4px 12px', 
                  borderRadius: '12px',
                  background: relaxationPhase === 'closed' ? 'rgba(255,170,0,0.2)' : 'rgba(0,170,255,0.2)',
                  color: relaxationPhase === 'closed' ? '#ffaa00' : '#00aaff',
                  fontWeight: 'bold'
                }}>
                  {relaxationPhase === 'open' ? '👁️ OJOS ABIERTOS' : '😌 OJOS CERRADOS'}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ opacity: 0.5 }}>α </span>
                    <span style={{ color: '#ffaa00', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {currentMetrics.bands.alpha?.toFixed(1) || '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ opacity: 0.5 }}>β </span>
                    <span style={{ color: '#00aaff' }}>
                      {currentMetrics.bands.beta?.toFixed(1) || '—'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <button onClick={skipCalibration} style={{ marginTop: '10px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', cursor: 'pointer' }}>
              Saltar →
            </button>
          </>
        )}
        
        {calibrationStep === CALIBRATION_STEPS.COMPLETE && calibrationResults && (
          <div>
            <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>{calibrationResults.passed ? '✅' : '⚠️'}</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: calibrationResults.passed ? '#00ff88' : '#ffaa00', marginBottom: '10px' }}>
              {calibrationResults.passed ? 'CALIBRACIÓN EXITOSA' : 'CALIBRACIÓN PARCIAL'}
            </div>
            <div style={{ fontSize: '0.7rem', opacity: 0.8, marginBottom: '10px' }}>{calibrationResults.reason}</div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.7rem', textAlign: 'left', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px' }}>
              <div>
                <div style={{ opacity: 0.5 }}>Parpadeos</div>
                <div style={{ color: '#00ff88', fontWeight: 'bold' }}>{calibrationResults.blinksDetected} / {calibrationResults.targetBlinks}</div>
              </div>
              <div>
                <div style={{ opacity: 0.5 }}>Alpha ratio</div>
                <div style={{ color: parseFloat(calibrationResults.alphaRatio) > 1 ? '#00ff88' : '#ffaa00', fontWeight: 'bold' }}>
                  {calibrationResults.alphaRatio}x
                </div>
              </div>
              <div>
                <div style={{ opacity: 0.5 }}>α ojos abiertos</div>
                <div style={{ color: '#00aaff' }}>{calibrationResults.alphaOpen}</div>
              </div>
              <div>
                <div style={{ opacity: 0.5 }}>α ojos cerrados</div>
                <div style={{ color: '#ffaa00' }}>{calibrationResults.alphaClosed}</div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <ActionButton onClick={startCalibration} color="#ffaa00">↺ Recalibrar</ActionButton>
              <ActionButton onClick={async () => {
                await fetch(`${API_BASE}/set-mode/muse`, { method: 'POST' });
                if (onModeChange) onModeChange('muse');
                setCalibrationStep(CALIBRATION_STEPS.IDLE);
              }} color="#00ff88">▶ Continuar</ActionButton>
            </div>
          </div>
        )}
        
        {calibrationStep === CALIBRATION_STEPS.FAILED && calibrationResults && (
          <div>
            <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>❌</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#ff4444', marginBottom: '10px' }}>CALIBRACIÓN FALLIDA</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.8, marginBottom: '15px' }}>{calibrationResults.reason}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <ActionButton onClick={startCalibration} color="#ffaa00">↺ Reintentar</ActionButton>
              <ActionButton onClick={skipCalibration} color="#666">Continuar igual</ActionButton>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: 'rgba(0, 20, 40, 0.9)', border: `1px solid ${getStatusColor()}40`, borderRadius: '12px', padding: '15px', fontFamily: 'monospace', color: '#fff', minWidth: '280px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.2rem' }}>🎧</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>MUSE 2 EEG</span>
        </div>
        <div style={{ fontSize: '0.7rem', color: getStatusColor() }}>{getStatusText()}</div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(255, 50, 50, 0.2)', border: '1px solid rgba(255, 50, 50, 0.5)', borderRadius: '6px', padding: '8px', fontSize: '0.7rem', marginBottom: '10px', color: '#ff8888' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Instrucciones — solo si no se sabe qué hacer */}
      {status === MUSE_STATUS.DISCONNECTED && (
        <div style={{ marginBottom: '10px' }}>
          <button onClick={() => setShowInstructions(!showInstructions)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', cursor: 'pointer', padding: 0 }}>
            {showInstructions ? '▼' : '▶'} Instrucciones
          </button>

          {showInstructions && (
            <div style={{ marginTop: '8px', padding: '10px', background: 'rgba(0,100,200,0.1)', borderRadius: '6px', fontSize: '0.65rem', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 4px 0' }}>1. Enciende el Muse 2 (LED parpadeando azul)</p>
              <p style={{ margin: '0' }}>2. Haz click en <strong>Escanear</strong> — la UI busca y conecta directamente</p>
            </div>
          )}
        </div>
      )}

      {/* Device picker modal */}
      {showDeviceModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            background: 'rgba(0, 15, 30, 0.98)',
            border: '1px solid rgba(0,170,255,0.3)',
            borderRadius: '14px',
            padding: '20px',
            maxWidth: '360px',
            width: '90%',
            fontFamily: 'monospace'
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#00aaff' }}>Dispositivos encontrados</div>
                <div style={{ fontSize: '0.6rem', opacity: 0.45, marginTop: '2px' }}>
                  {discoveredDevices.length} Muse {discoveredDevices.length === 1 ? 'detectado' : 'detectados'}
                </div>
              </div>
              <button
                onClick={() => setShowDeviceModal(false)}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 }}
              >✕</button>
            </div>

            {/* Device list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {discoveredDevices.map((device) => {
                const isLast = device.address === lastMuseAddress;
                return (
                  <button
                    key={device.address}
                    onClick={() => connectToDevice(device.address)}
                    style={{
                      width: '100%', padding: '12px 14px',
                      background: isLast ? 'rgba(0,170,255,0.12)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${isLast ? 'rgba(0,170,255,0.45)' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '8px', color: '#fff', cursor: 'pointer',
                      textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = isLast ? 'rgba(0,170,255,0.22)' : 'rgba(255,255,255,0.09)'}
                    onMouseLeave={e => e.currentTarget.style.background = isLast ? 'rgba(0,170,255,0.12)' : 'rgba(255,255,255,0.04)'}
                  >
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>🎧</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 'bold', color: isLast ? '#00aaff' : '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {device.name || 'Muse'}
                        {isLast && (
                          <span style={{ fontSize: '0.55rem', background: 'rgba(0,170,255,0.25)', borderRadius: '4px', padding: '1px 5px', fontWeight: 'normal' }}>último</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.58rem', opacity: 0.4, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {device.address}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
                      {/* RSSI (batería se lee en el backend durante connect, no aquí) */}
                      {device.rssi !== undefined && (
                        <div style={{ fontSize: '0.58rem', opacity: 0.35 }}>{device.rssi} dBm</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Fallback: use existing LSL stream */}
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={async () => {
                  setShowDeviceModal(false);
                  setStatus(MUSE_STATUS.SCANNING);
                  setError(null);
                  try {
                    const res = await fetch(`${API_BASE}/hardware/connect-stream`, { method: 'POST' });
                    const data = await res.json();
                    if (data.status === 'success') {
                      setStatus(MUSE_STATUS.CONNECTED);
                      startStream();
                    } else {
                      setError('No se encontró stream LSL activo');
                      setStatus(MUSE_STATUS.ERROR);
                    }
                  } catch {
                    setError('No se encontró stream LSL activo');
                    setStatus(MUSE_STATUS.ERROR);
                  }
                }}
                style={{
                  width: '100%', padding: '8px',
                  background: 'transparent',
                  border: '1px dashed rgba(255,255,255,0.12)',
                  borderRadius: '6px', color: 'rgba(255,255,255,0.3)',
                  fontSize: '0.62rem', cursor: 'pointer', fontFamily: 'monospace'
                }}
              >
                Usar stream LSL existente (start_muse.sh)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device status — visible siempre que esté streaming o calibrando */}
      {(status === MUSE_STATUS.STREAMING || status === MUSE_STATUS.CALIBRATING) && renderDeviceStatus()}

      {/* Calibración */}
      {(status === MUSE_STATUS.CALIBRATING || calibrationStep === CALIBRATION_STEPS.COMPLETE || calibrationStep === CALIBRATION_STEPS.FAILED) && renderCalibration()}

      {/* Signal quality */}
      {status === MUSE_STATUS.STREAMING && calibrationStep === CALIBRATION_STEPS.IDLE && renderSignalQuality()}

      {/* Live EEG bands — visible while streaming (idle OR calibrating) */}
      {status === MUSE_STATUS.STREAMING && renderLiveBands()}
      {status === MUSE_STATUS.CALIBRATING && renderLiveBands()}

      {/* Recording section */}
      {status === MUSE_STATUS.STREAMING && calibrationStep === CALIBRATION_STEPS.IDLE && (
        <div style={{ marginTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '15px' }}>
          {isRecording ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Recording indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '10px', 
                  height: '10px', 
                  borderRadius: '50%', 
                  background: '#ff4444',
                  animation: 'pulse 1s infinite'
                }} />
                <span style={{ fontSize: '0.75rem', color: '#ff4444', fontWeight: 'bold' }}>
                  ● REC {formatTime(recordingTime)}
                </span>
              </div>
              
              {/* Quick markers */}
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  onClick={() => addMarker('evento')} 
                  style={{ 
                    background: 'rgba(255,170,0,0.2)', 
                    border: '1px solid rgba(255,170,0,0.5)', 
                    borderRadius: '4px', 
                    padding: '4px 8px', 
                    fontSize: '0.6rem', 
                    color: '#ffaa00', 
                    cursor: 'pointer' 
                  }}
                  title="Agregar marcador"
                >
                  📍
                </button>
              </div>
              
              {/* Stop button */}
              <button
                onClick={stopRecording}
                style={{
                  marginLeft: 'auto',
                  padding: '8px 12px',
                  background: 'rgba(255,68,68,0.2)',
                  border: '1px solid #ff4444',
                  borderRadius: '6px',
                  color: '#ff4444',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                ⏹ Detener
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowRecordingModal(true)}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(255,68,68,0.1)',
                border: '1px dashed rgba(255,68,68,0.5)',
                borderRadius: '8px',
                color: '#ff6666',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              🔴 Grabar Sesión
            </button>
          )}
          
          {/* Recording modal */}
          {showRecordingModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'rgba(0, 20, 40, 0.95)',
                border: '1px solid rgba(255,68,68,0.5)',
                borderRadius: '12px',
                padding: '20px',
                maxWidth: '350px',
                width: '90%'
              }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '1rem', color: '#ff6666' }}>
                  🔴 Nueva Grabación
                </h3>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block', marginBottom: '5px' }}>
                    Nombre de la sesión
                  </label>
                  <input
                    type="text"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    placeholder="Mi sesión de meditación..."
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.8rem',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block', marginBottom: '5px' }}>
                    Notas (opcional)
                  </label>
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    placeholder="Experimento con respiración profunda..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      resize: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowRecordingModal(false)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'rgba(100,100,100,0.2)',
                      border: '1px solid rgba(100,100,100,0.5)',
                      borderRadius: '6px',
                      color: '#888',
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={startRecording}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'rgba(255,68,68,0.2)',
                      border: '1px solid #ff4444',
                      borderRadius: '6px',
                      color: '#ff4444',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    🔴 Iniciar
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.3; }
            }
          `}</style>
        </div>
      )}

      {/* Botones */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '15px', flexWrap: 'wrap' }}>
        {status === MUSE_STATUS.DISCONNECTED && (
          <ActionButton onClick={scanDevices} color="#00aaff">� Escanear Muse</ActionButton>
        )}

        {status === MUSE_STATUS.SCANNING && (
          <ActionButton disabled color="#666">
            {scanCountdown > 0 ? `🔍 Escaneando ${scanCountdown}s...` : '⏳ Conectando...'}
          </ActionButton>
        )}
        
        {status === MUSE_STATUS.CONNECTED && (
          <>
            <ActionButton onClick={startStream} color="#00ff88">▶ Iniciar + Calibrar</ActionButton>
            <ActionButton onClick={disconnect} color="#ff6666">✕</ActionButton>
          </>
        )}
        
        {status === MUSE_STATUS.STREAMING && calibrationStep === CALIBRATION_STEPS.IDLE && (
          <>
            <ActionButton onClick={startCalibration} color="#ffaa00">🎯 Recalibrar</ActionButton>
            <ActionButton onClick={disconnect} color="#ff6666">✕ Desconectar</ActionButton>
          </>
        )}
        
        {status === MUSE_STATUS.ERROR && (
          <ActionButton onClick={() => { setError(null); scanDevices(); }} color="#00aaff">↺ Reconectar</ActionButton>
        )}
      </div>
    </div>
  );
}

function ActionButton({ children, onClick, color, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        minWidth: '80px',
        padding: '10px 12px',
        background: disabled ? 'rgba(100,100,100,0.3)' : `${color}20`,
        border: `1px solid ${disabled ? 'rgba(100,100,100,0.3)' : color}`,
        borderRadius: '8px',
        color: disabled ? '#666' : color,
        fontSize: '0.75rem',
        fontFamily: 'monospace',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        fontWeight: 'bold'
      }}
      onMouseEnter={(e) => { if (!disabled) e.target.style.background = `${color}40`; }}
      onMouseLeave={(e) => { if (!disabled) e.target.style.background = `${color}20`; }}
    >
      {children}
    </button>
  );
}
